import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Roles} from '@models/roles.enum';
import {BehaviorSubject, catchError, map, Observable, of, tap} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {LogginedUser} from '@models/user.model';
import {Router} from '@angular/router';
import {ChatService} from "@shared/services/chat/chat.service";

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    authUrl = `${environment.apiUrl}`;
    baseV2AuthUrl = `${environment.baseApiUrl}v2/auth`;
    logginedUser: LogginedUser;

    private loggedUser: BehaviorSubject<LogginedUser> = new BehaviorSubject<LogginedUser>(null);
    public loggedUser$: Observable<LogginedUser> = this.loggedUser.asObservable();

    constructor(
        public http: HttpClient,
        private router: Router,
        private chatService: ChatService,
    ) {
    }

    get isUserLogged(): boolean {
        return !!this.getToken();
    }

    get isUserPrelogged(): boolean {
        return !!this.getPretoken();
    }

    public getToken(): string | null {
        return localStorage.getItem('token');
    }

    public getPretoken(): string | null {
        return localStorage.getItem('pretoken');
    }

    get roles(): Roles[] {
        const token = this.getToken();
        if (token) {
            return this.parseJwt(token).roles;
        }
        return [];
    }

    public refreshLoggedUser() {
        this.getLogginedUser(true).subscribe()
    }

    public getLogginedUser(force: boolean = false): Observable<LogginedUser> {
        if (!this.isUserLogged) {
            return of(null);
        }
        if (this.logginedUser && !force) {
            return of(this.logginedUser);
        }
        return this.http.get(`${this.authUrl}users/me`)
            .pipe(
                map((res: { data: LogginedUser }) => {
                    if (!res.data) {
                        return null;
                    }
                    this.logginedUser = new LogginedUser(res?.data);
                    this.loggedUser.next(this.logginedUser)
                    return this.logginedUser;
                }));
    }

    getUserType(): string {
        const token = this.parseJwt(this.getToken())
        return token["type"]
    }

    getCurrentType(): string {
        return localStorage.getItem('type')
    }

    setCurrentType(type: string) {
        localStorage.setItem('type', type)
    }

    public signIn(email: string, password: string, deviceInfo: object): Observable<boolean> {
        const payload = {email, password, deviceInfo};
        return new Observable<boolean>(subscriber => {
            this.http.post(`${this.baseV2AuthUrl}/sign-in/step-one`, payload).subscribe({
                next: (res: any) => {
                    if (res && res.data && res.data.token) {
                        localStorage.setItem('pretoken', res.data.token);
                        subscriber.next(true)
                    } else {
                        subscriber.next(false)
                    }
                },
                error: (error) => {
                    subscriber.error(error)
                },
                complete: () => {
                    subscriber.complete()
                }
            });
        })
    }

    public stepTwo(pretoken: string, otp: number, deviceInfo): Observable<boolean> {
        const payload = {
            token: pretoken,
            otp: otp,
            deviceInfo: deviceInfo
        }
        return new Observable<boolean>(subscriber => {
            this.http.post(`${this.baseV2AuthUrl}/sign-in/step-two`, payload).subscribe({
                next: (res: any) => {
                    if (res && res.data && res.data.token) {
                        localStorage.setItem('token', res.data.token);
                        const userType = this.getUserType()
                        if (userType == "both") {
                            localStorage.setItem('type', '');
                        } else {
                            localStorage.setItem('type', userType);
                        }
                        subscriber.next(true)
                    } else {
                        subscriber.next(false)
                    }
                },
                error: (error) => {
                    subscriber.error(error)
                },
                complete: () => {
                    subscriber.complete()
                }
            });
        })
    }

    public forgotPassword(email: string): Observable<{
        message: string
    }> {
        const payload = {email};
        return this.http.post<{ message: string }>(`${this.authUrl}forgot-password`, payload);
    }

    public clearData(): void {
        localStorage.clear();
        this.logginedUser = null;
    }

    public signOut(): Observable<any> {
        this.logginedUser = null;
        this.chatService.logout()
        return this.http.post(`${this.authUrl}auth/sign-out`, {})
            .pipe(
                tap(() => {
                    this.clearData();
                    this.router.navigate(['/signin']);
                }),
                catchError((error) => {
                    this.clearData();
                    this.router.navigate(['/signin']);
                    return of(error);
                })
            );
    }

    parseJwt(token): {
        roles: Roles[],
    } {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    public resetPassword(token: string, password: string): Observable<{
        message: string
    }> {
        const payload = {
            password,
            passwordRepeat: password,
            token,
        };
        return this.http.post<{ message: string }>(`${this.authUrl}restore-password`, payload);
    }
}
